import * as React from "react";
import { PaletteMode } from "@mui/material";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import koinTextOnlyLogo from "../../assets/koin_text_only_logo.png";



interface AppAppBarProps {
  mode: PaletteMode;
  toggleColorMode: () => void;
  isBannerVisible?: boolean;
  showProfile?: boolean; // New prop to control profile visibility
  useTextLogo?: boolean; // New prop to toggle between Text and Image logo
  simpleNavbar?: boolean; // New prop to hide desktop links and show hamburger always
  transparentMode?: boolean; // New prop for transparent background without pill shape
  showLogin?: boolean; // New prop to control login button visibility (default true)
  sectionLinks?: boolean; // Landing page only: desktop links to page sections + download button
}

function AppAppBar({ mode, toggleColorMode, isBannerVisible, showProfile = false, useTextLogo = false, simpleNavbar = false, transparentMode = false, showLogin = true, sectionLinks = false }: AppAppBarProps) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const sectionElement = document.getElementById(sectionId);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth" });
      setOpen(false);
    }
  };

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          boxShadow: transparentMode && !scrolled ? "none" : "0 12px 32px -14px rgba(31, 41, 55, 0.22)",
          bgcolor: transparentMode && !scrolled ? "transparent" : "rgba(255, 255, 255, 0.92)",
          backdropFilter: transparentMode && !scrolled ? "none" : "blur(12px)",
          backgroundImage: "none",
          border: transparentMode && !scrolled ? "1px solid transparent" : "1px solid #F3E6CF",
          transition: "background-color 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease, border 0.3s ease",

          // Static Floating Pill Styles
          width: "min(95%, 1200px)",
          left: "50%",
          transform: "translateX(-50%)",
          top: "16px",
          borderRadius: "50px",
          padding: "0",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="regular"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
              minHeight: "60px",
              px: "16px !important",
            }}
          >
            {/* Logo Logic */}
            {useTextLogo ? (
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => window.location.href = "/"}>
                <Typography variant="h5" sx={{ color: '#EF6C00', fontWeight: 800, fontFamily: "General-Sans-bold, sans-serif" }}>
                  App<span style={{ color: '#FFC107' }}>20</span>
                </Typography>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '2px', marginBottom: '14px' }}>
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#EF6C00" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="img"
                src="/koin_logo_yellow.png"
                alt=""
                sx={{ height: { xs: 34, md: 40 }, width: "auto", cursor: "pointer" }}
                onClick={() => window.location.href = "/"}
              />
              <Box
                component="img"
                src={koinTextOnlyLogo}
                sx={{
                  width: { xs: "95px", md: "115px" }, // Adjusted slightly so it matches normal layout sizing comfortably
                  height: "auto",
                  cursor: "pointer",
                }}
                alt="logo of KoinApp"
                onClick={() => window.location.href = "/"}
                data-track="image"
                data-name="koinapp_header_logo"
              />
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

              {sectionLinks && (
                <Box component="nav" aria-label="Main" sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5, mr: 1.5 }}>
                  {[
                    { label: "How it works", href: "#how-it-works" },
                    { label: "FAQ", href: "#faq" },
                    { label: "Help", href: "/help" }
                  ].map((l) => (
                    <Button
                      key={l.label}
                      href={l.href}
                      sx={{ color: "#374151", fontWeight: 600, fontSize: "0.95rem", textTransform: "none", borderRadius: "999px", px: 2, "&:hover": { background: "#FFF3DC", color: "#C2410C" } }}
                    >
                      {l.label}
                    </Button>
                  ))}
                  <Button
                    href="https://play.google.com/store/apps/details?id=com.koinapp.co&hl=en_IN"
                    target="_blank"
                    sx={{
                      ml: 1,
                      color: "#FFF",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      textTransform: "none",
                      borderRadius: "999px",
                      px: 2.75,
                      py: 1,
                      background: "linear-gradient(90deg, #FFB300 0%, #F57C00 100%)",
                      boxShadow: "0 8px 18px -8px rgba(245, 124, 0, 0.6)",
                      "&:hover": { background: "linear-gradient(90deg, #FFA000 0%, #E65100 100%)" }
                    }}
                  >
                    Download KOIN
                  </Button>
                </Box>
              )}

              {/* Profile Icon - Visible if showProfile is true */}
              {/* Profile Icon - Visible if showProfile is true */}
              {/* {simpleNavbar && showLogin && (
                     <Button
                        onClick={() => window.location.href = "/login"}
                        sx={{ 
                            display: "flex", 
                            color: "#EF6C00", 
                            fontWeight: 700, 
                            textTransform: "none",
                            fontSize: "18px",
                            minWidth: "auto",
                            p: 1,
                            mr: 0.5
                        }}
                     >
                        Login
                     </Button>
                )} */}

              {showProfile && (
                <Button
                  onClick={() => window.location.href = "/profile"}
                  sx={{ minWidth: "auto", p: 1 }}
                >
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Head circle */}
                    <circle cx="14" cy="9" r="5" stroke="#EF6C00" strokeWidth="3" fill="none" />
                    {/* Body arc */}
                    <path d="M4 24C4 18.5 8.5 14 14 14C19.5 14 24 18.5 24 24" stroke="#EF6C00" strokeWidth="3" strokeLinecap="round" fill="none" />
                  </svg>
                </Button>
              )}

              {/* Mobile Menu Icon or All Screens if Simple Navbar */}
              <Box sx={{ display: { xs: "flex", md: simpleNavbar && !sectionLinks ? "flex" : "none" } }}>
                <Button
                  onClick={toggleDrawer(true)}
                  aria-label="Open menu"
                  sx={{ minWidth: "auto", p: 1 }}
                >
                  <svg width="28" height="18" viewBox="0 0 28 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="28" height="3" rx="1.5" fill="#EF6C00" />
                    <rect y="7.5" width="28" height="3" rx="1.5" fill="#EF6C00" />
                    <rect x="8" y="15" width="20" height="3" rx="1.5" fill="#EF6C00" />
                  </svg>
                </Button>
              </Box>
            </Box>

            {/* Desktop Menu - Hide if simpleNavbar is true */}
            {!simpleNavbar && (
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2 }}>
                <MenuItem onClick={() => scrollToSection("testimonials")}>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>Testimonials</Typography>
                </MenuItem>
                <MenuItem onClick={() => scrollToSection("offers-section")}>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>Offers</Typography>
                </MenuItem>
                {/* {showLogin && (
                   <MenuItem onClick={() => window.location.href = "/login"}>
                      <Typography variant="body2" color="text.primary" fontWeight={600}>Login</Typography>
                   </MenuItem>
               )} */}
                <Button
                  variant="contained"
                  onClick={() => window.location.href = "https://offer.app20.in/apk"}
                  sx={{
                    backgroundColor: "#EF6C00",
                    color: "#fff",
                    borderRadius: "20px",
                    textTransform: "none",
                    ml: 2,
                    boxShadow: "0 2px 8px rgba(239, 108, 0, 0.3)",
                    "&:hover": { backgroundColor: "#E65100" }
                  }}
                  startIcon={<FileDownloadOutlinedIcon />}
                >
                  Download App
                </Button>
              </Box>
            )}

            <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
              <Box sx={{ minWidth: "72vw", maxWidth: 360, p: 2.5, backgroundColor: "#fff", height: "100%", display: "flex", flexDirection: "column", "& .MuiMenuItem-root": { borderRadius: "12px", py: 1.5, "& .MuiTypography-root": { fontWeight: 600, color: "#1F2937" }, "&:hover": { background: "#FFF8EC" } } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box component="img" src="/koin_logo_yellow.png" alt="" sx={{ height: 34 }} />
                  <Button onClick={toggleDrawer(false)} aria-label="Close menu" sx={{ minWidth: "auto", p: 1, color: "#1F2937", borderRadius: "50%" }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                  </Button>
                </Box>

                {showProfile && (
                  <MenuItem onClick={() => window.location.href = "/profile"}>
                    <Typography variant="body1" color="text.primary">Profile</Typography>
                  </MenuItem>
                )}

                {/* <MenuItem onClick={() => scrollToSection("testimonials")}>
                  <Typography variant="body1" color="text.primary">Testimonials</Typography>
                </MenuItem> */}
                {/* <MenuItem onClick={() => scrollToSection("offers-section")}>
                  <Typography variant="body1" color="text.primary">Offers</Typography>
                </MenuItem> */}
                {/* {showLogin && (
                        <MenuItem onClick={() => window.location.href = "/login"}>
                            <Typography variant="body1" color="text.primary">Login</Typography>
                        </MenuItem>
                    )} */}
                <MenuItem onClick={() => window.location.href = "/help"}>
                  <Typography variant="body1" color="text.primary">Help</Typography>
                </MenuItem>
                <MenuItem onClick={() => window.location.href = "/terms-and-conditions"}>
                  <Typography variant="body1" color="text.primary">Terms & Conditions</Typography>
                </MenuItem>
                <MenuItem onClick={() => window.location.href = "/privacy-policy"}>
                  <Typography variant="body1" color="text.primary">Privacy Policy</Typography>
                </MenuItem>
                <MenuItem onClick={() => window.location.href = "/contact-us"}>
                  <Typography variant="body1" color="text.primary">Contact Us</Typography>
                </MenuItem>
                <Button
                  href="https://play.google.com/store/apps/details?id=com.koinapp.co&hl=en_IN"
                  target="_blank"
                  sx={{ mt: "auto", color: "#FFF", fontWeight: 700, textTransform: "none", borderRadius: "999px", py: 1.4, background: "linear-gradient(90deg, #FFB300 0%, #F57C00 100%)" }}
                >
                  Download KOIN
                </Button>
                {/* <MenuItem onClick={() => window.location.href = "https://offer.app20.in/apk"}>
                  <Typography variant="body1" color="text.primary">Download APK</Typography>
                  <FileDownloadOutlinedIcon sx={{ ml: 1, color: "#EF6C00" }} />
                </MenuItem> */}

              </Box>
            </Drawer>

          </Toolbar>
        </Container>
      </AppBar>
    </div>
  );
}

export default AppAppBar;
